import { useState, useEffect, type FC, type ReactNode } from "react";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/utils/core";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { PhoneNumberInput } from "@/shared/components/ui/phone-number-input";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { DatePicker } from "@/shared/components/ui/date-picker";
import MainLayout from "@/pages/layouts/MainLayout";
import { PageActions } from "@/pages/layouts/components/PageActions";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useCanonicalUserAddress } from "@/shared/hooks/useCanonicalAddress";
import { AddressPicker } from "@/shared/components/ui/AddressPicker";
import { buildAddress, parseAddress, EMPTY_ADDRESS } from "@/shared/utils/addressUtils";
import { buildFullName } from "@/shared/utils/names";

import type { AddressParts } from "@/shared/utils/addressUtils";
import { useUpdateUser, useUpdateNotifications } from "@/shared/hooks/useUpdateUser";
import { usePaymentTier } from "@/shared/hooks/usePaymentTier";
import {
  formatPhoneNumber,
  formatPersonDateForDisplay,
  cleanSSN,
  isValidSSN,
} from "@/shared/utils/formatters";
import { useSnackbar } from "notistack";
import {
  Tooltip,
  TooltipContent,
  TooltipInfoTrigger,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Switch } from "@/shared/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { notifyAccountDeletion } from "@/shared/utils/slackNotifications";
import { useNotificationFlags } from "@/shared/hooks/useNotificationFlags";
import { trackJourneyEvent, trackEvent, EventType } from "@/shared/utils/core";
import { sentryUtils } from "@/shared/utils/sentry";
import { supabase } from "@/shared/lib/supabase";
import { logActivity } from "@/shared/utils/activityLogger";
import { SSNField } from "@/shared/components/SSNField";
import { SSN_VALIDATION_MESSAGE, useSSNField } from "@/shared/hooks/useSSNField";
import { useRegularSSNData } from "@/shared/hooks/useRegularSSNData";
import { useSaveRegularSSN } from "@/shared/hooks/useSaveRegularSSN";
import { WHY_WE_ASK_TITLE } from "@/shared/constants/whyWeAsk";

interface FieldProps {
  label: string;
  isEditing: boolean;
  /** Links the label to its control. Must match the id on the child input. */
  id?: string;
  /** Read-only display. Omit when children render both states themselves. */
  value?: ReactNode;
  children?: ReactNode;
  className?: string;
  tooltip?: string;
}

const Field: FC<FieldProps> = ({ label, isEditing, id, value, children, className, tooltip }) => {
  const tooltipTrigger = tooltip && (
    <TooltipProvider>
      <Tooltip>
        <TooltipInfoTrigger aria-label={WHY_WE_ASK_TITLE} className="ml-1 align-middle" />
        <TooltipContent title={WHY_WE_ASK_TITLE}>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {isEditing ? (
        <Label htmlFor={id}>
          {label}
          {tooltipTrigger}
        </Label>
      ) : (
        <p className="text-sm text-secondary">
          {label}
          {tooltipTrigger}
        </p>
      )}
      {isEditing || value === undefined ? children : <p className="truncate">{value}</p>}
    </div>
  );
};

export default function SettingsUpdated() {
  const { authUser, user, person, refetch } = useAppContext();
  const { updateUser, isUpdating } = useUpdateUser();
  const { updateNotifications, isUpdating: isUpdatingNotifications } = useUpdateNotifications();
  const { enqueueSnackbar } = useSnackbar();
  const { tier, isActive, isLoading } = usePaymentTier(person?.estate_id);
  const { canSendNotification } = useNotificationFlags();
  const { last4 } = useRegularSSNData({ skipDecrypt: true });
  const { saveSSN, isLoading: isSavingSSN } = useSaveRegularSSN();
  const { ssnValue, setSsnValue, ssnVisible, setSsnVisible, ssnInputKey, remountInput } =
    useSSNField(null);
  const [isEditingSSN, setIsEditingSSN] = useState(false);
  const [ssnError, setSsnError] = useState("");

  const [isEditingBasicInfo, setIsEditingBasicInfo] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [basicInfoForm, setBasicInfoForm] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    phoneNumber: "",
    address: EMPTY_ADDRESS as AddressParts,
    dateOfBirth: "",
  });

  const { data: canonicalMailing } = useCanonicalUserAddress(user?.user_id);

  const seedAddress = (): AddressParts =>
    canonicalMailing
      ? {
          streetAddress: canonicalMailing.street1,
          apartment: canonicalMailing.street2 ?? "",
          city: canonicalMailing.city ?? "",
          state: canonicalMailing.state ?? "",
          zipCode: canonicalMailing.zip,
        }
      : parseAddress(user?.address || "");

  // Track page access
  useEffect(() => {
    trackEvent(EventType.SETTINGS_PAGE_ACCESSED, {
      has_subscription: isActive,
      subscription_status: tier || "none",
    });
  }, []);

  // Initialize form data when component mounts or data changes
  useEffect(() => {
    if (!isEditingBasicInfo) {
      setBasicInfoForm({
        firstName: user?.first_name || "",
        middleName: user?.middle_name || "",
        lastName: user?.last_name || "",
        phoneNumber: user?.phone_number || "",
        address: seedAddress(),
        dateOfBirth: user?.date_of_birth || "",
      });
    }
  }, [user, isEditingBasicInfo, canonicalMailing]);

  const handleEditBasicInfo = () => {
    setIsEditingBasicInfo(true);
    setPhoneError("");
  };

  const handleCancelEditBasicInfo = () => {
    setIsEditingBasicInfo(false);
    setPhoneError("");
    // Reset form to original values
    setBasicInfoForm({
      firstName: user?.first_name || "",
      middleName: user?.middle_name || "",
      lastName: user?.last_name || "",
      phoneNumber: user?.phone_number || "",
      address: seedAddress(),
      dateOfBirth: user?.date_of_birth || "",
    });
  };

  const validatePhoneNumber = (phone: string): boolean => {
    const digitsOnly = phone.replace(/\D/g, "");
    return digitsOnly.length === 10;
  };

  const handlePhoneNumberChange = (value: string) => {
    setBasicInfoForm((prev) => ({ ...prev, phoneNumber: value }));
    if (phoneError) {
      setPhoneError("");
    }
  };

  const handleSaveBasicInfo = async () => {
    if (basicInfoForm.phoneNumber && !validatePhoneNumber(basicInfoForm.phoneNumber)) {
      setPhoneError("Please enter a valid phone number");
      return;
    }

    try {
      const cleanPhoneNumber = basicInfoForm.phoneNumber.replace(/\D/g, "");

      updateUser({
        user: {
          first_name: basicInfoForm.firstName,
          middle_name: basicInfoForm.middleName || null,
          last_name: basicInfoForm.lastName,
          phone_number: cleanPhoneNumber,
          address: buildAddress(basicInfoForm.address),
          full_name: buildFullName(
            basicInfoForm.firstName,
            basicInfoForm.middleName,
            basicInfoForm.lastName,
          ),
          date_of_birth: basicInfoForm.dateOfBirth || null,
        },
      });

      // Track settings update
      trackJourneyEvent(EventType.SETTINGS_UPDATED, {
        section: "personal_details",
        fields_updated: [
          basicInfoForm.firstName !== (user?.first_name || "") ? "first_name" : null,
          basicInfoForm.middleName !== (user?.middle_name || "") ? "middle_name" : null,
          basicInfoForm.lastName !== (user?.last_name || "") ? "last_name" : null,
          cleanPhoneNumber !== (user?.phone_number || "") ? "phone_number" : null,
          buildAddress(basicInfoForm.address) !== (user?.address || "") ? "address" : null,
        ].filter(Boolean),
      });

      enqueueSnackbar("Personal details updated", { variant: "success" });
      setIsEditingBasicInfo(false);
      refetch();
    } catch (err) {
      enqueueSnackbar(
        "Your details weren't saved, so your earlier details are still in place. Try again.",
        { variant: "error" },
      );
      sentryUtils.captureError(err, "Failed to save personal details", {
        userId: user?.user_id,
        estateId: person?.estate_id,
      });
    }
  };

  const handleSaveSSN = async () => {
    if (!user?.user_id) return;

    if (!ssnValue || !isValidSSN(ssnValue)) {
      setSsnError(SSN_VALIDATION_MESSAGE);
      return;
    }

    try {
      await saveSSN({ ssn: cleanSSN(ssnValue), userId: user.user_id });
      enqueueSnackbar("Social Security number updated", { variant: "success" });
      setIsEditingSSN(false);
      setSsnError("");
      setSsnValue("");
      setSsnVisible(false);
      remountInput();
    } catch (err) {
      enqueueSnackbar("Your Social Security number wasn't saved. Try again.", {
        variant: "error",
      });
      sentryUtils.captureError(err, "User SSN save failed on settings page", {
        userId: user?.user_id,
        estateId: person?.estate_id,
      });
    }
  };

  const handleCancelSSN = () => {
    setIsEditingSSN(false);
    setSsnError("");
    setSsnValue("");
    setSsnVisible(false);
    remountInput();
  };

  const handleNotificationToggle = async (
    field: "contact_by_phone" | "contact_by_email",
    value: boolean,
  ) => {
    try {
      updateNotifications({ [field]: value });

      // Track notification settings update
      trackJourneyEvent(EventType.SETTINGS_UPDATED, {
        section: "notifications",
        field: field,
        enabled: value,
      });

      enqueueSnackbar("Notification preferences updated", {
        variant: "success",
      });
      refetch();
    } catch (err) {
      enqueueSnackbar("Your notification settings weren't changed. Try again.", {
        variant: "error",
      });
      sentryUtils.captureError(err, "Failed to update notification preferences", {
        userId: user?.user_id,
        estateId: person?.estate_id,
      });
    }
  };

  const handleDeleteAccount = async () => {
    if (!authUser || !user) return;

    try {
      await supabase.from("users").update({ requested_deletion: true }).eq("auth_id", authUser.id);

      if (person?.estate_id && user?.user_id) {
        logActivity({
          estate_id: person.estate_id,
          action_type: "requested_account_deletion",
          user_id: user.user_id,
          entity_type: "user",
          entity_id: String(user.user_id),
          metadata: { email: authUser.email ?? null },
        }).catch(() => {});
      }
    } catch (error) {
      console.error("Failed to set requested_deletion:", error);
      sentryUtils.captureError(error, "Failed to mark account for deletion", {
        userId: user?.user_id,
        estateId: person?.estate_id,
      });
    }

    if (canSendNotification(authUser.email)) {
      try {
        await notifyAccountDeletion();
      } catch (error) {
        console.error("Failed to send account deletion notification:", error);
        sentryUtils.captureError(
          error,
          "Account deletion notification failed",
          { userId: user?.user_id, estateId: person?.estate_id },
          "warning",
        );
      }
    }

    enqueueSnackbar("Account deletion request submitted", { variant: "info" });
    setIsDeleteDialogOpen(false);
  };

  return (
    <MainLayout header="Settings" actions={<PageActions />}>
      <div className="flex flex-col w-full lg:max-w-[70%] mx-auto ">
        <Card className="w-full mb-6 border border-primary">
          <CardHeader className="p-6 border-b border-primary">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold">Your personal details</CardTitle>
              <div className="flex items-center gap-3">
                {isEditingBasicInfo ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={handleCancelEditBasicInfo}
                      disabled={isUpdating}
                    >
                      Cancel
                    </Button>
                    <Button variant="default" onClick={handleSaveBasicInfo} disabled={isUpdating}>
                      {isUpdating ? "Saving..." : "Save"}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="ghost"
                    onClick={handleEditBasicInfo}
                    className="text-brand font-semibold hover:no-underline"
                  >
                    Edit
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-8">
              <Field
                label="First name"
                isEditing={isEditingBasicInfo}
                id="settings-first-name"
                value={basicInfoForm.firstName || "—"}
              >
                <Input
                  id="settings-first-name"
                  value={basicInfoForm.firstName}
                  onChange={(e) =>
                    setBasicInfoForm((prev) => ({
                      ...prev,
                      firstName: e.target.value,
                    }))
                  }
                  disabled={isUpdating}
                />
              </Field>
              <Field
                label="Middle name"
                isEditing={isEditingBasicInfo}
                id="settings-middle-name"
                value={basicInfoForm.middleName || "—"}
              >
                <Input
                  id="settings-middle-name"
                  value={basicInfoForm.middleName}
                  onChange={(e) =>
                    setBasicInfoForm((prev) => ({
                      ...prev,
                      middleName: e.target.value,
                    }))
                  }
                  disabled={isUpdating}
                />
              </Field>
              <Field
                label="Last name"
                isEditing={isEditingBasicInfo}
                id="settings-last-name"
                value={basicInfoForm.lastName || "—"}
              >
                <Input
                  id="settings-last-name"
                  value={basicInfoForm.lastName}
                  onChange={(e) =>
                    setBasicInfoForm((prev) => ({
                      ...prev,
                      lastName: e.target.value,
                    }))
                  }
                  disabled={isUpdating}
                />
              </Field>
              <Field
                label="Date of birth"
                isEditing={isEditingBasicInfo}
                id="settings-dob"
                value={
                  basicInfoForm.dateOfBirth
                    ? formatPersonDateForDisplay(basicInfoForm.dateOfBirth, {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                      })
                    : "—"
                }
              >
                <DatePicker
                  id="settings-dob"
                  dateString={basicInfoForm.dateOfBirth || undefined}
                  onChange={() => {}}
                  onDateStringChange={(val) =>
                    setBasicInfoForm((prev) => ({ ...prev, dateOfBirth: val || "" }))
                  }
                  placeholder="MM/DD/YYYY"
                />
              </Field>
              <Field
                label="Email address"
                isEditing={isEditingBasicInfo}
                id="settings-email"
                value={authUser?.email || "—"}
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Input id="settings-email" value={authUser?.email || ""} disabled={true} />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-white text-sm">Contact us to change your email</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Field>
              <Field
                label="Phone number"
                isEditing={isEditingBasicInfo}
                id="settings-phone"
                value={formatPhoneNumber(basicInfoForm.phoneNumber)}
              >
                <div>
                  <PhoneNumberInput
                    id="settings-phone"
                    value={basicInfoForm.phoneNumber}
                    onChange={handlePhoneNumberChange}
                    className={phoneError ? "border-destructive" : undefined}
                    disabled={isUpdating}
                  />
                  <FieldError message={phoneError} className="mt-1" />
                </div>
              </Field>
              <div className={isEditingBasicInfo ? "sm:col-span-2" : undefined}>
                {isEditingBasicInfo ? (
                  <div className={isUpdating ? "pointer-events-none opacity-60" : undefined}>
                    <AddressPicker
                      value={basicInfoForm.address}
                      onChange={(address) => setBasicInfoForm((prev) => ({ ...prev, address }))}
                      labels={{ streetAddress: "Address", apartment: "Apt, suite, etc." }}
                      stateFormat="abbreviation"
                      equalColumns
                    />
                  </div>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    <p className="text-sm text-secondary">Address</p>
                    <p className="truncate">{buildAddress(basicInfoForm.address) || "—"}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="w-full mb-6 border border-primary">
          <CardHeader className="p-6 border-b border-primary">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold">Sensitive information</CardTitle>
              <div className="flex items-center gap-3">
                {isEditingSSN ? (
                  <>
                    <Button variant="outline" onClick={handleCancelSSN} disabled={isSavingSSN}>
                      Cancel
                    </Button>
                    <Button variant="default" onClick={handleSaveSSN} disabled={isSavingSSN}>
                      {isSavingSSN ? "Saving..." : "Save"}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="ghost"
                    onClick={() => setIsEditingSSN(true)}
                    className="text-brand font-semibold hover:no-underline"
                  >
                    Edit
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <Field
              label="Social Security number"
              tooltip="Some forms ask for your own number, like the IRS form that names you as the person in charge of the estate and the survivor benefits application. Being named on it doesn't change your own taxes. Saving it here means you enter it once. It's encrypted, and only you can see it."
              isEditing={isEditingSSN}
            >
              <div>
                <SSNField
                  isEditing={isEditingSSN}
                  valueFormatted={ssnValue}
                  onChangeFormatted={setSsnValue}
                  savedValue=""
                  ssnVisible={ssnVisible}
                  toggleVisible={() => setSsnVisible(!ssnVisible)}
                  inputKey={ssnInputKey}
                  last4={last4}
                />
                <FieldError message={ssnError} className="mt-1" />
              </div>
            </Field>
          </CardContent>
        </Card>

        <Card className="w-full mb-6 border border-primary">
          <CardHeader className="p-6 border-b border-primary">
            <CardTitle className="text-lg font-semibold">Notifications</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-6">
            <div className="space-y-8">
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1.5">
                  <Label>Email notifications</Label>
                  <p className="text-sm text-secondary">Get updates by email</p>
                </div>
                <Switch
                  checked={user?.contact_by_email ?? true}
                  onCheckedChange={(checked) =>
                    handleNotificationToggle("contact_by_email", checked)
                  }
                  disabled={isUpdatingNotifications}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1.5">
                  <Label>SMS notifications</Label>
                  <p className="text-sm text-secondary">Get updates by text</p>
                </div>
                <Switch
                  checked={user?.contact_by_phone ?? false}
                  onCheckedChange={(checked) =>
                    handleNotificationToggle("contact_by_phone", checked)
                  }
                  disabled={isUpdatingNotifications}
                />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="w-full mb-6 border border-primary">
          <CardHeader className="p-6 border-b border-primary">
            <CardTitle className="text-lg font-semibold">Your plan</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex flex-col gap-1.5">
              <Label>Current plan</Label>
              <p className="text-base">
                {isLoading
                  ? "Loading..."
                  : tier === "platinum"
                    ? "Estate Settlement"
                    : tier === "premium"
                      ? "Asset Search"
                      : isActive
                        ? "Estate Setup"
                        : "Free"}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="w-full mb-6 border border-primary">
          <CardHeader className="p-6 border-b border-primary">
            <CardTitle className="text-lg font-semibold text-red-600">Delete account</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-6">
            <div className="space-y-4">
              <p className="text-sm text-secondary">
                This deletes your account and the information you've added to Elayne. Once it's
                done, it can't be undone.
              </p>
              <Button
                variant="destructive"
                className="w-full md:w-auto"
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                Delete account
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="rounded-lg w-[90%] bg-white h-auto">
          <DialogHeader className="bg-white border-none pb-0">
            <DialogTitle className="!text-lg font-semibold font-sans">
              Delete your account?
            </DialogTitle>
          </DialogHeader>
          <div className="mx-8 pt-2 pb-0">
            <p>Your account and the information you've added to Elayne will be deleted for good.</p>
          </div>
          <div className="flex items-end pt-6">
            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <Button
                variant="outline"
                onClick={() => setIsDeleteDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteAccount} className="flex-1">
                Delete account
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
